// Lightweight, provider-agnostic event tracker.
// Fires to whichever analytics library is loaded at runtime:
//   - Google Analytics 4 (gtag.js)          → window.gtag('event', ...)
//   - Google Tag Manager                     → window.dataLayer.push({...})
//   - Plausible                              → window.plausible(name, { props })
// Safe no-op when nothing is installed, and safe during SSR.

import { useEffect } from "react";

type EventProps = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    gtag?: (command: "event" | "config" | "js", eventName: string, params?: EventProps) => void;
    dataLayer?: Array<Record<string, unknown> | IArguments>;
    plausible?: (event: string, options?: { props?: EventProps }) => void;
    __plunketsAnalyticsInit?: boolean;
  }
}

function pageContext(): EventProps {
  if (typeof window === "undefined") return {};
  return {
    page_location: window.location.href,
    page_title: typeof document !== "undefined" ? document.title : undefined,
    page_path: window.location.pathname,
  };
}

export function trackEvent(name: string, props: EventProps = {}): void {
  if (typeof window === "undefined") return;

  const enriched: EventProps = { event_name: name, ...pageContext(), ...props };

  try {
    window.gtag?.("event", name, enriched);
    window.dataLayer?.push({ event: name, ...enriched });
    window.plausible?.(name, { props: enriched });
  } catch {
    // Never let analytics break UX.
  }
}

/** Fire once on mount — useful for logical "page view" style events on route entry. */
export function useTrackEvent(name: string, props: EventProps = {}): void {
  // Serialize deps to avoid effect re-fires for stable object literals.
  const key = JSON.stringify(props);
  useEffect(() => {
    trackEvent(name, props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, key]);
}

/**
 * Install global delegated click listeners for phone / WhatsApp links.
 * Idempotent — safe to call from React effects on every mount.
 */
export function initAnalytics(): void {
  if (typeof window === "undefined") return;
  if (window.__plunketsAnalyticsInit) return;
  window.__plunketsAnalyticsInit = true;

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") ?? "";
      const text = (anchor.textContent ?? "").trim().slice(0, 80) || undefined;

      if (href.startsWith("tel:")) {
        trackEvent("phone_click", {
          phone_number: href.replace(/^tel:/, ""),
          button_text: text,
          link_url: href,
        });
        return;
      }

      if (/(^|\/\/)(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com|chat\.whatsapp\.com)/i.test(href)) {
        trackEvent("whatsapp_click", {
          button_text: text,
          link_url: href,
        });
      }
    },
    { capture: true },
  );
}
